import { useQuery } from "@tanstack/react-query";
import { Typography, Input } from "antd";
import { Fragment } from "react/jsx-runtime";
import { Controller, FieldErrors, useFormContext } from "react-hook-form";
import { journalsQueries } from "@/entities/journal";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import { useTitleAmendmentMode } from "../../../../model";
import { CustomDatePicker } from "./ui";
import "./construction-details.scss";

const { TextArea } = Input;

interface IConstructionDetails {
  errors: FieldErrors<{
    journalnumber: string;
    projectCharacteristics: string;
    constructionStartDate: string;
    constructionEndDate: string;
    mainJournalStartDate: string;
    mainJournalEndDate: string;
  }>;
}

export const ConstructionDetails = ({ errors }: IConstructionDetails) => {
  const { control } = useFormContext();
  const { titleId } = useCoordinator();
  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));

  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canEditEmptyField = (value?: string | null) =>
    titleStatus || (isNewAmendment && !value);
  return (
    <Fragment>
      <div className="construction-details__characteristics">
        <Typography.Text>
          Краткие проектные характеристики объекта
        </Typography.Text>
        <Can I="edit" a="title_journals">
          <Controller
            name="projectCharacteristics"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                placeholder="Введите характеристики"
                rows={4}
                disabled={
                  !canEditEmptyField(
                    data?.mainJournal?.titleJournal?.projectCharacteristics,
                  )
                }
              />
            )}
          />
        </Can>

        <Can not I="edit" a="title_journals">
          <Controller
            name="projectCharacteristics"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                placeholder="Введите характеристики"
                rows={4}
                disabled
              />
            )}
          />
        </Can>

        {errors.projectCharacteristics && (
          <Typography.Text className="construction-details__error">
            {errors.projectCharacteristics.message}
          </Typography.Text>
        )}
      </div>

      <div className="construction-details__dates-section">
        <div className="construction-details__dates-row">
          <Can I="edit" a="title_journals">
            <CustomDatePicker
              name="constructionStartDate"
              label="Начало строительства"
              disabled={
                !canEditEmptyField(
                  data?.mainJournal?.titleJournal?.constructionStartDate,
                )
              }
            />
          </Can>

          <Can not I="edit" a="title_journals">
            <CustomDatePicker
              name="constructionStartDate"
              label="Начало строительства"
              disabled
            />
          </Can>

          <Can I="edit" a="title_journals">
            <CustomDatePicker
              name="constructionEndDate"
              label="Окончание строительства"
              disableBefore="constructionStartDate"
              disabled={
                !canEditEmptyField(
                  data?.mainJournal?.titleJournal?.constructionEndDate,
                )
              }
              // disabled={!watch("constructionStartDate")}
            />
          </Can>

          <Can not I="edit" a="title_journals">
            <CustomDatePicker
              name="constructionEndDate"
              label="Окончание строительства"
              disableBefore="constructionStartDate"
              disabled
            />
          </Can>
        </div>
      </div>

      <div className="construction-details__journal-period">
        <div className="construction-details__journal-dates">
          <Can I="edit" a="title_journals">
            <CustomDatePicker
              name="mainJournalStartDate"
              label="Начало журнала"
              disabled={
                !canEditEmptyField(
                  data?.mainJournal?.titleJournal?.mainJournalStartDate,
                )
              }
            />
          </Can>
          <Can not I="edit" a="title_journals">
            <CustomDatePicker
              name="mainJournalStartDate"
              label="Начало журнала"
              disabled
            />
          </Can>
          <Can I="edit" a="title_journals">
            <CustomDatePicker
              name="mainJournalEndDate"
              label="Окончание журнала"
              disableBefore="mainJournalStartDate"
              disabled={
                !canEditEmptyField(
                  data?.mainJournal?.titleJournal?.mainJournalEndDate,
                )
              }
            />
          </Can>
          <Can not I="edit" a="title_journals">
            <CustomDatePicker
              name="mainJournalEndDate"
              label="Окончание журнала"
              disableBefore="mainJournalStartDate"
              disabled
            />
          </Can>
        </div>
      </div>
    </Fragment>
  );
};
